import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Server, Download, Shield, CheckCircle2, AlertTriangle, XCircle,
  Globe, FileText, Eye, RefreshCw, Code, Rocket, Copy
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';



const STATUS_COLORS = { healthy: 'badge-aktif', warning: 'badge-duraklatildi', down: 'badge-hata', unchecked: 'badge-taslak' };
const STATUS_LABELS = { healthy: 'Saglikli', warning: 'Uyari', down: 'Erisim Yok', unchecked: 'Kontrol Edilmedi' };

export default function DeploymentPage() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [stagingReport, setStagingReport] = useState(null);
  const [trafficScore, setTrafficScore] = useState(null);
  const [nginxConfig, setNginxConfig] = useState('');
  const [showNginx, setShowNginx] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/deploy/sites-status');
      setSites(data);
      if (data.length > 0 && !selectedSite) setSelectedSite(data[0].site_id);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedSite]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadSiteData = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const [reportRes, scoreRes] = await Promise.all([
        api.get(`/deploy/staging-report?site_id=${selectedSite}`),
        api.get(`/traffic/score?site_id=${selectedSite}`),
      ]);
      setStagingReport(reportRes.data);
      setTrafficScore(scoreRes.data);
    } catch (err) { console.error(err); }
  }, [selectedSite]);

  useEffect(() => { loadSiteData(); }, [loadSiteData]);

  const loadNginx = async () => {
    try {
      const { data } = await api.get(`/deploy/nginx-config?site_id=${selectedSite}`);
      setNginxConfig(data);
      setShowNginx(true);
    } catch (err) { toast.error('Nginx config yuklenemedi'); }
  };

  const downloadDeploy = () => {
    const token = localStorage.getItem('trafficos_token');
    window.open(`/api/deploy/package?site_id=${selectedSite}&token=${token}`, '_blank');
  };

  const copyConfig = () => {
    navigator.clipboard.writeText(nginxConfig);
    toast.success('Nginx config kopyalandi');
  };

  const selectedSiteData = sites.find(s => s.site_id === selectedSite);

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Deploy Yonetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">Production deploy + staging dogrulama</p>
        </div>
        <Select value={selectedSite} onValueChange={setSelectedSite}>
          <SelectTrigger className="w-[260px] bg-secondary/40 border-border/70"><SelectValue placeholder="Site secin" /></SelectTrigger>
          <SelectContent>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.brand_name} ({s.domain})</SelectItem>)}</SelectContent>
        </Select>
      </motion.div>

      {/* Sites Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {sites.map(s => (
          <Card key={s.site_id} className={`border-border/70 card-hover cursor-pointer ${s.site_id === selectedSite ? 'gold-glow' : ''}`} onClick={() => setSelectedSite(s.site_id)}>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium truncate">{s.brand_name}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[s.domain_status] || 'badge-taslak'}`}>
                  {STATUS_LABELS[s.domain_status] || 'Bilinmiyor'}
                </span>
              </div>
              <p className="text-xs font-mono text-muted-foreground">{s.domain}</p>
              <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
                <span>{s.pages_total} sayfa</span>
                <span>{s.pages_published} yayinda</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedSite && (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Traffic Score */}
          {trafficScore && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="xl:col-span-4">
              <Card className="border-border/70 h-full">
                <CardHeader><CardTitle className="text-base">Trafik Guvenlik Skoru</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-center mb-4">
                    <div className={`text-5xl font-bold font-['Space_Grotesk'] ${trafficScore.score >= 75 ? 'text-emerald-400' : trafficScore.score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                      {trafficScore.score}
                    </div>
                    <div className={`text-lg font-semibold mt-1 ${trafficScore.grade === 'A' ? 'text-emerald-400' : trafficScore.grade === 'B' ? 'text-[hsl(var(--primary))]' : 'text-amber-400'}`}>
                      Grade {trafficScore.grade}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{trafficScore.recommendation}</p>
                  </div>
                  <div className="space-y-2">
                    {trafficScore.factors?.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{f.factor}</span>
                        <span className={f.status === 'ok' ? 'text-emerald-400' : f.status === 'warning' ? 'text-amber-400' : f.status === 'critical' ? 'text-red-400' : 'text-muted-foreground'}>
                          {f.score}/{f.max}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Staging Report */}
          {stagingReport && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="xl:col-span-8">
              <Card className="border-border/70">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Staging Dogrulama Raporu</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{stagingReport.domain}</p>
                  </div>
                  <span className={`text-sm px-3 py-1 rounded-full font-medium ${stagingReport.deploy_ready ? 'badge-aktif' : 'badge-hata'}`}>
                    {stagingReport.deploy_ready ? 'Deploy Hazir' : 'Hazir Degil'}
                  </span>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {stagingReport.checks?.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary/20">
                        <div className="flex items-center gap-2">
                          {c.status === 'ok' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> :
                           c.status === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-400" /> :
                           c.status === 'critical' ? <XCircle className="w-4 h-4 text-red-400" /> :
                           <Globe className="w-4 h-4 text-muted-foreground" />}
                          <span className="text-sm">{c.check}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{String(c.value)}</span>
                      </div>
                    ))}
                  </div>
                  {stagingReport.issues?.length > 0 && (
                    <div className="border-t border-border/50 pt-3 space-y-1">
                      {stagingReport.issues.map((issue, i) => (
                        <p key={i} className="text-xs text-red-400">• {issue}</p>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-3 mt-4">
                    <Button onClick={downloadDeploy} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] gap-2 press-active">
                      <Download className="w-4 h-4" /> Deploy Paketi Indir
                    </Button>
                    <Button onClick={loadNginx} variant="outline" className="gap-2 border-border/70">
                      <Code className="w-4 h-4" /> Nginx Config
                    </Button>
                    <Button onClick={loadSiteData} variant="ghost" size="icon"><RefreshCw className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      )}

      {/* Nginx Config Dialog */}
      <Dialog open={showNginx} onOpenChange={setShowNginx}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Nginx Konfigurasyonu
              <Button onClick={copyConfig} size="sm" variant="outline" className="gap-1.5 text-xs">
                <Copy className="w-3 h-3" /> Kopyala
              </Button>
            </DialogTitle>
          </DialogHeader>
          <pre className="bg-secondary/40 rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre text-muted-foreground leading-relaxed">
            {nginxConfig}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
